import { useEffect, useState } from "react";
import { api, formatMoney, formatDate } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import RescheduleBillModal from "@/components/RescheduleBillModal";
import { CalendarClock } from "lucide-react";

export default function PayPeriods() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduling, setRescheduling] = useState(null);

  const load = async () => {
    const { data } = await api.get("/forecast/pay-periods");
    setPeriods(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="pay-periods-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Pay periods</div>
        <h1 className="font-heading text-3xl font-medium text-[#2C302E] mt-1">Between each paycheck</h1>
        <p className="text-[#59605C] mt-1 max-w-xl">
          Bills are grouped under the paycheck that covers them. Each card shows the pay-period window, scheduled income, accumulated bills, and the estimated balance left.
        </p>
      </div>

      {loading ? <div className="text-[#59605C]">Loading…</div> : periods.length === 0 ? (
        <div className="card-soft p-10 text-center text-[#59605C]" data-testid="pay-periods-empty">
          Add a recurring income (weekly / biweekly / monthly) to see pay periods.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {periods.map((p, i) => (
            <div key={i} className="card-soft p-6" data-testid={`pay-period-card-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <StatusBadge status={p.status_label} size="sm" testId={`pp-status-${i}`} />
                <span className="text-xs text-[#8A918D] uppercase tracking-widest">{p.frequency}</span>
              </div>
              <div className="font-heading text-lg font-medium text-[#2C302E]">{p.source}</div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D] mt-2">Pay period</div>
              <div className="text-sm font-medium text-[#2C302E]">
                {formatDate(p.period_start)} → {formatDate(p.period_end)}
              </div>
              <div className="text-xs text-[#8A918D] mt-0.5">Paycheck arrives {formatDate(p.pay_date)}</div>

              <div className="mt-4 space-y-1.5 text-sm">
                <Row label="Scheduled income" value={`+${formatMoney(p.expected_income_amount)}`} pos />
                <Row label="Bills due in this period" value={`-${formatMoney(p.assigned_bills_total)}`} />
                {Number(p.assigned_pending_total) > 0 && (
                  <Row label="Pending charges" value={`-${formatMoney(p.assigned_pending_total)}`} />
                )}
                <div className="pt-2 mt-1 border-t border-[#EBE6E0] flex justify-between font-semibold">
                  <span className="text-[#2C302E]">Estimated balance</span>
                  <span className={p.projected_leftover < 0 ? "text-[#8C3A30]" : "text-[#3B5B3E]"}>
                    {formatMoney(p.projected_leftover)}
                  </span>
                </div>
              </div>

              {p.assigned_bills?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#EBE6E0]">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#8A918D] mb-2">Bills in this period</div>
                  <div className="space-y-1.5">
                    {p.assigned_bills.map((b) => (
                      <div key={b.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`pp-${i}-bill-${b.id}`}>
                        <div className="min-w-0 flex-1 truncate">
                          <span className="text-[#59605C]">{b.name} · {formatDate(b.due_date)}</span>
                          {b.original_due_date && (
                            <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-widest px-1 py-0.5 rounded-full bg-[#EDE7F6] text-[#4A3B7A] align-middle">
                              <CalendarClock size={8} /> rescheduled
                            </span>
                          )}
                        </div>
                        <span className="text-[#2C302E] font-medium">{formatMoney(b.estimated_due_amount)}</span>
                        <button
                          className="btn-ghost !px-2 !py-0.5 text-[10px] text-[#8A918D] hover:text-[#4A6148] hover:bg-transparent whitespace-nowrap"
                          onClick={() => setRescheduling(b)}
                          title="Move this instance without changing the recurring day"
                          data-testid={`pp-${i}-reschedule-${b.id}`}
                        >
                          <CalendarClock size={11} /> Reschedule due date-This time
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <ReassurancePill status={p.status_label} leftover={p.projected_leftover} />
            </div>
          ))}
        </div>
      )}

      {rescheduling && (
        <RescheduleBillModal
          bill={rescheduling}
          onClose={() => setRescheduling(null)}
          onUpdated={() => { setRescheduling(null); load(); }}
          onBulkUpdated={() => { setRescheduling(null); load(); }}
        />
      )}
    </div>
  );
}

function Row({ label, value, pos }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#59605C]">{label}</span>
      <span className={pos ? "text-[#3B5B3E]" : "text-[#59605C]"}>{value}</span>
    </div>
  );
}

function ReassurancePill({ status, leftover }) {
  const msg = {
    safe: `You’re covered — about ${formatMoney(leftover)} left after bills.`,
    tight: `Just enough — cushion is thin (${formatMoney(leftover)}).`,
    short: `Short by ${formatMoney(Math.abs(leftover))} in this period.`,
  }[status];
  return (
    <div className={`mt-4 text-sm p-3 rounded-xl status-${status}`}>{msg}</div>
  );
}
